import { minimumIntentFields } from "@lafayette/domain";
import {
  conciergeIntakeRequestTypeValues,
  type StaffCreateRequestBodyInput,
} from "@lafayette/schemas";

import { typeLabel } from "../../requests/labels";

// The form of "New request" (CHT-2051, ADR 0018) as data: the request types, the fields that a type needs, the check before a send and the
// body. The type list is the one of the customer path (`conciergeIntakeRequestTypeValues`), and the fields a type NEEDS are the domain's own
// `minimumIntentFields`, so the form asks exactly for what the server will ask for. The server decides again: a refusal of a field comes back
// as a 422 and the form stays open.

type RequestTypeValue = (typeof conciergeIntakeRequestTypeValues)[number];

export const requestTypeOptions: readonly Readonly<{ value: string; label: string }>[] =
  conciergeIntakeRequestTypeValues.map((value) => ({ value, label: typeLabel(value) }));

const isRequestType = (value: string): value is RequestTypeValue =>
  (conciergeIntakeRequestTypeValues as readonly string[]).includes(value);

/** The fields a type may show, in the order of the form. The names are the closed codes of the intake contract. */
const FIELD_ORDER = [
  "dateFrom",
  "dateTo",
  "preferredTime",
  "timeZone",
  "partySize",
  "locationText",
  "details.pickupLocation",
  "details.dropoffLocation",
  "specialRequests",
] as const;
export type FieldKey = (typeof FIELD_ORDER)[number];

export type FormValues = Readonly<Record<"requestType" | "title" | FieldKey, string>>;
export const EMPTY_VALUES: FormValues = {
  requestType: "",
  title: "",
  dateFrom: "",
  dateTo: "",
  preferredTime: "",
  timeZone: "",
  partySize: "",
  locationText: "",
  "details.pickupLocation": "",
  "details.dropoffLocation": "",
  specialRequests: "",
};

export type FieldErrors = Readonly<Partial<Record<keyof FormValues, string>>>;

export type TypeField = Readonly<{ key: FieldKey; required: boolean }>;

/**
 * The fields to show for a type: every field the type needs (required), then the notes (optional unless the type needs them). A type that is
 * not chosen yet shows none.
 */
export function fieldsFor(type: string): readonly TypeField[] {
  if (!isRequestType(type)) return [];
  const needed = new Set<string>(minimumIntentFields[type]);
  return FIELD_ORDER.filter((key) => needed.has(key) || key === "specialRequests").map((key) => ({
    key,
    required: needed.has(key),
  }));
}

const REQUIRED_WORDS = "This is needed for this type of request.";

/** The checks the form can make alone. Each message says what to do. The server checks the rest (a date in the past, an unknown time zone). */
export function validateForm(values: FormValues): FieldErrors {
  const errors: { -readonly [K in keyof FormValues]?: string } = {};
  if (!isRequestType(values.requestType)) errors.requestType = "Choose a type of request.";
  if (values.title.trim() === "") errors.title = "Write a short title.";
  for (const { key, required } of fieldsFor(values.requestType)) {
    if (required && values[key].trim() === "") errors[key] = REQUIRED_WORDS;
  }
  const party = values.partySize.trim();
  if (party !== "" && (!/^\d+$/.test(party) || Number(party) < 1)) {
    errors.partySize = "Enter a whole number of 1 or more.";
  }
  if (values.dateTo !== "" && values.dateFrom !== "" && values.dateTo < values.dateFrom) {
    errors.dateTo = "The end date is before the start date.";
  }
  return errors;
}

/** The body of the route. An empty field is left out, and a field the type does not show is never sent. */
export function bodyOf(values: FormValues): StaffCreateRequestBodyInput {
  const type = values.requestType as RequestTypeValue;
  const shown = new Set<FieldKey>(fieldsFor(type).map((field) => field.key));
  const text = (key: FieldKey) => (shown.has(key) ? values[key].trim() : "");
  const requirements: StaffCreateRequestBodyInput["requirements"] = { title: values.title.trim() };
  for (const key of [
    "dateFrom",
    "dateTo",
    "preferredTime",
    "timeZone",
    "locationText",
    "specialRequests",
  ] as const) {
    const value = text(key);
    if (value !== "") requirements[key] = value;
  }
  const party = text("partySize");
  if (party !== "") requirements.partySize = Number(party);
  const details: Record<string, string> = {};
  const pickup = text("details.pickupLocation");
  const dropoff = text("details.dropoffLocation");
  if (pickup !== "") details.pickupLocation = pickup;
  if (dropoff !== "") details.dropoffLocation = dropoff;
  if (Object.keys(details).length > 0) requirements.details = details;
  return { requestType: type, requirements };
}

export const fieldLabels: Readonly<Record<FieldKey, string>> = {
  dateFrom: "Start date",
  dateTo: "End date",
  preferredTime: "Preferred time",
  timeZone: "Time zone of the place",
  partySize: "Party size",
  locationText: "Place",
  "details.pickupLocation": "Pick-up place",
  "details.dropoffLocation": "Drop-off place",
  specialRequests: "Notes for the concierge",
};
